import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  ClickAwayListener,
  Grid,
  SelectChangeEvent,
  Switch,
  styled as MUIStyled,
  Box,
  Button,
} from "@mui/material";
import { COLOR_MAP } from "../../../../constants/ColorNames";
import styled from "@emotion/styled";
import Tabs from "./Tabs";
import FollowUpBanner from "./FollowUpBanner";
import Subject from "./Subject";
import EmailContent from "./EmailContent";
import GenerateByAiButton from "./GenerateByAiButton";
import insertTextAtCursor from "insert-text-at-cursor";
import {
  useGenerateJDByAiMutation,
  folderNameApi,
  useGetInventoryPurchasedQuery,
  useGetSignatureQuery,
  usePostAiEmailMutation,
  usePostRephraseEmailMutation,
} from "../../../../redux/services/folderName";
import {
  setActiveTab,
  setCampaignTemplate,
  setContentPersonalisationGenratedViaAI,
  setContentPersonalisationType,
  setCurrentCount,
  setIsCampaignTemplateClicked,
  setRephraseCount,
  setRephrasedEmailObj,
  setSelectedContentPersonalisationKey,
  setSignature,
  setTabStates,
  setViewed,
  setJobDescription,
  setPreviews,
  setPreviewNo,
  resetEmailData,
  setTemplateSelected,
  setTempalteForCampaign,
  setPassiveJobs,
  setJDGeneratedByAi,
  setCompleteEmailReset,
  setResetDetails,
  setContentTypes,
} from "../../../../redux/slice/campaignSlice";
import {
  CampaignPlaceHolderDetails,
  TabStatesProps,
} from "../../../../types/dataTypes";
import { RootState } from "../../../../redux/store";
import BasicModal from "../../../../common/components/Modal";
import ProhibitedContent from "./ProhibitedContent";
import ForbiddenWordsStrip from "./ForbiddenWordsStrip";
import {
  ChannelInfo,
  getChannelId,
  removeErrorSpan,
  replaceDetails,
  replaceSpansWithPlaceholders,
  returnSingularOrPlural,
  runContentReveal,
} from "../../../../utils/helpers";
import {
  INITIAL_CAMPAIGN_PLACEHOLDERS,
  MULTIPLE_FOLLOW_UP,
  campaignPlaceholderMapping,
  placeholderMapping,
} from "../../../../constants/helpers";
import { ToastContext } from "../../../../utils/context/toastContext";
import {
  handleContentEditHelper,
  tabStateAfterAddFUP,
  tabStateAfterFUPDelete,
  updatedTabStateAfterPlaceholderInput,
  updatedTabStateForRadioButtons,
} from "../../../../utils/emailHelper";
import { CampaignPlaceholderInput } from "./CampaignPlaceholderInput";
import EmailFooter from "./EmailFooter";
import { EraseIcon } from "../../../../constants/icons/EraseIcon";
import { renderToStaticMarkup } from "react-dom/server";
import ContentEditable from "react-contenteditable";
import GeneratedByAiStrip from "./GeneratedByAiStrip";
import { PASSIVE_JOBS_ICON } from "../../../../constants/icons/PassiveJobsIcon";
import CustomText from "../../../../style/typography";
import { BootstrapTooltip } from "../../../../style/tooltips";
import { setJobPreview } from "../../../../redux/slice/previewSlice";
import sanitizeHtml from "../../../../utils/utils";
import { eventPushFunction } from "@foundit/clevertap";
import processHtmlForEmail from "../../../../utils/processHtmlForEmail";

const StyledTag = styled.span`
  border: 0.2rem solid ${COLOR_MAP.GREEN};
  border-radius: 0.4rem;
`;

const Container = styled(Grid)`
  justify-content: space-evenly;
  margin-top: 1.6rem;
  background-color: ${COLOR_MAP.WHITE};
  border-radius: 0.8rem;
  border: 0.1rem solid ${COLOR_MAP.BORDER_GREY};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;
const PassiveJobsContainer = styled.div`
  background-color: ${COLOR_MAP.GREY_8};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 16px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  font-family: "Noto Sans";
  gap: 8px;
  border: 1px solid ${COLOR_MAP.GREY_6};
  border-radius: 0 0 8px 8px;
`;

const PassiveSwitch = MUIStyled(Switch)(({ theme }) => ({
  width: 32,
  height: 16,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      color: COLOR_MAP.DARK_PURPLE,
      transform: "translateX(16px)",
      "& .MuiSwitch-thumb:before": {
        content: "''",
        position: "absolute",
        width: "100%",
        height: "100%",
        left: 0,
        top: 0,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="8" width="8" viewBox="0 0 8 8"><path fill="purple" d="M7.31821 1.31428C7.53121 1.49001 7.56143 1.80515 7.3857 2.01816L3.80066 6.36366C3.32069 6.94544 2.42934 6.94545 1.94937 6.36366L0.614327 4.74543C0.438595 4.53242 0.468814 4.21728 0.681822 4.04155C0.894831 3.86582 1.20997 3.89604 1.3857 4.10905L2.72074 5.72728C2.80074 5.82424 2.94929 5.82424 3.02929 5.72728L6.61433 1.38177C6.79006 1.16876 7.1052 1.13855 7.31821 1.31428Z"/></svg>')`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: COLOR_MAP.DARK_PURPLE,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: "white",
    width: 12,
    height: 12,
    borderRadius: 6,
    "&::before": {
      backgroundImage: "none",
    },
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: COLOR_MAP.BORDER_GREY,
    boxSizing: "border-box",
  },
}));

const CustomList = styled.ul`
  list-style: none;
  padding: 12px;
  font-size: 14px;
  line-height: 22px;
  & > li {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M1.29279 7.99999C1.29279 4.2957 4.29571 1.29279 8 1.29279C11.7043 1.29279 14.7072 4.2957 14.7072 7.99999C14.7072 11.7043 11.7043 14.7072 8 14.7072C4.29571 14.7072 1.29279 11.7043 1.29279 7.99999ZM11.5968 6.19138C11.7921 5.99612 11.7921 5.67954 11.5968 5.48428C11.4015 5.28902 11.085 5.28902 10.8897 5.48428L6.91892 9.45505L5.11031 7.64644C4.91505 7.45118 4.59847 7.45118 4.4032 7.64644C4.20794 7.8417 4.20794 8.15828 4.4032 8.35355L6.56537 10.5157C6.76063 10.711 7.07721 10.711 7.27247 10.5157L11.5968 6.19138Z' fill='%232BA682'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position-y: 3.5px;
    padding-left: 17px;
    margin: 3px 0;
  }
`;

const ResetContainer = styled(Box)`
  border-radius: 32px;
  border: 1px solid ${COLOR_MAP.BORDER_GREY};
  background: #fff;
  box-shadow:
    0px 4px 4px -2px rgba(0, 0, 0, 0.08),
    0px 6px 8px -3px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  gap: 5.5px;
  align-items: center;
  line-height: 20px;
  font-size: 14px;
  padding: 12px 16px;
`;

const Email = () => {
  const { setToastValue } = useContext(ToastContext);
  const [openModal, setOpenModal] = useState(false);
  const [isBannerClose, setIsBannerClose] = useState(false);
  const [openResetModal, setOpenResetModal] = useState(false);
  const [activeElement, setActiveElement] = useState("");
  const [deletedTabLabel, setDeletedTabLabel] = useState("");
  const [togglePlaceHolder, setTogglePlaceHolder] = useState(false);
  const [isPlaceHolderAdded, setIsPlaceHolderAdded] = useState(false);
  const [revealingContent, setRevealingContent] = useState<boolean>(false);
  const [revealingContentTab, setRevealingContentTab] =
    useState<string>("Email");
  const [revealedEmail, setRevealedEmail] = useState("");
  const [revealingJD, setRevealingJD] = useState<boolean>(false);
  const [revealedJD, setRevealedJD] = useState("");
  const [selectedTones, setSelectedTones] = useState<any>({});
  const [selectedLengths, setSelectedLengths] = useState<any>({});
  const [showCampaignPlaceholder, setShowCampaignPlaceholder] =
    useState<boolean>(false);
  const [placeHolderDetails, setPlaceHolderDetails] =
    useState<CampaignPlaceHolderDetails>({
      top: 0,
      left: 0,
      content: "",
      value: "",
    });
  const [campiagnPlaceholderValues, setCampaignPlaceholderValues] =
    useState<any>(INITIAL_CAMPAIGN_PLACEHOLDERS);
  const [isContentRevealedByTemplates, setIsContentRevealedByTemplates] =
    React.useState<boolean>(false);
  const isAiContentGenratedByPreValues = React.useRef(false);
  const isAiJDGeneratedByPreValues = React.useRef(false);
  const [showGenerateByAiOnFollowUp, setShowGenerateByAiOnFollowUp] = useState<
    string[]
  >([]);

  const dispatch = useDispatch();
  const [postAiEmail, { isLoading: isAiDataLoading }] =
    usePostAiEmailMutation();
  const [aiJD, { isLoading: isAiJDLoading }] = useGenerateJDByAiMutation();
  const [postRephraseEmail, { isLoading: isRephraseDataLoading }] =
    usePostRephraseEmailMutation();
  const userData = useSelector((state: RootState) => state.campaign.userData);
  const { data: signatureData } = useGetSignatureQuery(1);
  const userDetails = useSelector(
    (state: RootState) => state.campaign.userDetails,
  );
  const contentTypes = useSelector(
    (state: RootState) => state.campaign.contentTypes,
  );

  const tabState = useSelector((state: RootState) => state.campaign.tabState);
  const signature = useSelector((state: RootState) => state.campaign.signature);
  const jobDescription = useSelector(
    (state: RootState) => state.campaign.jobDescription,
  );
  const selectedTab = useSelector(
    (state: RootState) => state.campaign.selectedTab,
  );
  const isCampaignTemplateClicked = useSelector(
    (state: RootState) => state.campaign.isCampaignTemplateClicked,
  );
  const { rephraseCount, currentCount, viewed, rephrasedEmailObj } =
    useSelector((state: RootState) => state.campaign.emailGeneratedState);
  const { isBulkCampaign, campaignInitiationId } = useSelector(
    (state: RootState) => state.campaign.campaignCreationQueryData,
  );
  const selectedTabData = tabState.find(
    (tab: TabStatesProps) => tab.label === selectedTab,
  );
  const jobTitle = useSelector((state: RootState) => state.campaign.jobTitle);
  const minExperience = useSelector(
    (state: RootState) => state.campaign.minExperience,
  );
  const maxExperience = useSelector(
    (state: RootState) => state.campaign.maxExperience,
  );
  const jobLocation = useSelector(
    (state: RootState) => state.campaign.jobLocation,
  );
  const jobMode = useSelector((state: RootState) => state.campaign.jobMode);
  const isBridgeCustomer = useSelector(
    (state: RootState) => state.campaign.isBridgeCustomer,
  );
  const resetDetails = useSelector(
    (state: RootState) => state.campaign.resetDetails,
  );
  const showFollowUpBanner = !isBannerClose && tabState?.length > 1;
  const showForbiddenStrip = selectedTabData?.showError;

  const activeActionButton =
    selectedTabData?.applyButton && selectedTabData.replyButton
      ? "both"
      : selectedTabData?.applyButton
        ? "apply"
        : selectedTabData?.replyButton
          ? "reply"
          : "";
  const disableGenerateContent =
    isAiDataLoading ||
    isRephraseDataLoading ||
    !isAiContentGenratedByPreValues.current;
  const email = selectedTabData?.email || "";
  const companyData: any = useSelector(
    (state: RootState) =>
      state?.folderNameApi?.queries["getCompanyDetails(1)"]?.data,
  );
  const passiveJobsEnabled = useSelector(
    (state: RootState) => state.campaign.passiveJobs,
  );
  const showPassiveJobs = useSelector(
    (state: RootState) => state.campaign.showPassiveJobs,
  );

  const { contentPersonalisationGenratedViaAI, JDGeneratedByAi } = useSelector(
    (state: RootState) => state.campaign,
  );

  const handleTabClick = (selectedTab: string) => {
    // setSelectedTab(selectedTab)
    dispatch(setActiveTab({ selectedTab: selectedTab }));
  };
  const subjectRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const emailRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const signatureRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const jdRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const urlParams = new URLSearchParams(window.location.search);
  const campaignId = urlParams.get("campaignId");
  const initiationId = urlParams.get("initiationId");
  const templateSelected = useSelector(
    (state: RootState) => state.campaign.templateSelected,
  );

  const rePhraseFeature = rephraseCount === 0; // added to not show the rephrase button
  const disablePassiveJobs = useMemo(() => {
    return (
      !contentPersonalisationGenratedViaAI ||
      !JDGeneratedByAi ||
      templateSelected
    );
  }, [JDGeneratedByAi, contentPersonalisationGenratedViaAI, templateSelected]);

  const cn = urlParams.get("cn") as string;
  const { domain }: ChannelInfo = getChannelId(cn);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        campaignId ||
        !jobTitle ||
        !minExperience ||
        !maxExperience ||
        (!jobLocation?.length && jobMode !== "Remote") ||
        (selectedTabData?.email && !isAiUsed) ||
        (tabState?.[0]?.email && !isAiUsed)
      ) {
        return;
      }

      handleGenerate(true);
    }, 800);

    return () => clearTimeout(timer);
  }, [jobTitle, minExperience, maxExperience, jobLocation?.length, jobMode]);

  useEffect(() => {
    if (
      !isAiContentGenratedByPreValues?.current &&
      selectedTabData?.alreadyGenerated
    ) {
      isAiContentGenratedByPreValues.current = true;
    }
  }, [selectedTabData?.alreadyGenerated]);

  //Commenting default follow-up for this release
  useEffect(() => {
    if (!campaignId) {
      addMultipleFollowUp();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    if (!disablePassiveJobs) {
      dispatch(setPassiveJobs(!!companyData?.isPassiveJobsEnabled));
    }
  }, [companyData?.isPassiveJobsEnabled, disablePassiveJobs, dispatch]);

  useEffect(() => {
    if (!isCampaignTemplateClicked) return;

    dispatch(setActiveTab({ selectedTab: "Email" }));
    setRevealingContentTab("Email");
    runContentRevealForTemplates(tabState[0]?.email);
    setCampaignPlaceholderValues(INITIAL_CAMPAIGN_PLACEHOLDERS);
    dispatch(
      setIsCampaignTemplateClicked({ isCampaignTemplateClicked: false }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCampaignTemplateClicked]);

  useEffect(() => {
    if (signatureData && signatureData.sign) {
      dispatch(setSignature({ signature: signatureData.sign }));
    }
  }, [dispatch, signatureData]);

  // to set the carat after placeholder click
  useEffect(() => {
    if (!activeElement || !isPlaceHolderAdded) return;
    let el;
    if (activeElement === "subject") {
      el = subjectRef.current;
    } else if (activeElement === "email") {
      el = emailRef.current;
    } else if (activeElement === "signature") {
      el = signatureRef.current;
    } else {
      el = jdRef.current;
    }

    let range = document.createRange();
    let sel = window.getSelection()!;
    range.setStart(el, el.childNodes.length);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }, [activeElement, isPlaceHolderAdded, togglePlaceHolder]);

  const addMultipleFollowUp = () => {
    const newTabStates = JSON.parse(JSON.stringify(tabState));
    const anyTabState = newTabStates[0];
    MULTIPLE_FOLLOW_UP.forEach((item, index) => {
      let email = item.mail;
      let subject = item.subject;

      Object.entries(placeholderMapping).forEach(([text, htmlContent]) => {
        let updatedText = text.split(":")[0];

        if (email.includes(updatedText)) {
          email = email.replace(updatedText, htmlContent);
        }
        if (subject.includes(updatedText)) {
          subject = subject.replace(updatedText, htmlContent);
        }
      });

      Object.entries(campaignPlaceholderMapping).forEach(
        ([text, htmlContent]) => {
          if (email.includes(text)) {
            email = email.replaceAll(text, htmlContent);
          }
          if (subject.includes(text)) {
            subject = subject.replace(text, htmlContent);
          }
        },
      );

      newTabStates.push({
        label: `Follow up-${index + 1}`,
        selectedFollowUPdays: item.selectedFollowUPdays,
        subject: subject,
        email: email,
        applyButton: anyTabState?.applyButton,
        replyButton: anyTabState?.replyButton,
        stepNumber: index + 2,
      });
    });
    dispatch(setTabStates({ tabState: [...newTabStates] }));
  };

  const handleAddFollowUp = () => {
    if (revealingContent || revealedJD) return;

    const newTabStates = tabStateAfterAddFUP(tabState);
    const newTabLabel = `Follow up-${newTabStates.length - 1}`;

    dispatch(setTabStates({ tabState: [...newTabStates] }));
    dispatch(setActiveTab({ selectedTab: newTabLabel }));
    if (contentPersonalisationGenratedViaAI === 1) {
      handleFollowup({
        index: newTabStates.length - 1,
        tabStates: newTabStates,
        selectedTabLabel: newTabLabel,
      });
    } else {
      setShowGenerateByAiOnFollowUp((prev) => [...prev, newTabLabel]);
    }
  };

  const handleDeleteModalButton = () => {
    setOpenModal(false);
    handleDeleteFollowup();
  };

  const handleDeleteFollowup = () => {
    const newTabStates = tabStateAfterFUPDelete(tabState, deletedTabLabel);
    dispatch(setTabStates({ tabState: [...newTabStates] }));
    dispatch(setActiveTab({ selectedTab: newTabStates.at(-1).label }));
  };

  const handleOpenModal = (label?: string) => {
    if (label) {
      setDeletedTabLabel(label);
    }
    setOpenModal(true);
  };

  const handleActionsButtonsChange = React.useCallback(
    (event: ChangeEvent<Element>, value: string) => {
      const newTabStates = updatedTabStateForRadioButtons(tabState, value);
      dispatch(setTabStates({ tabState: [...newTabStates] }));
    },
    [tabState, dispatch],
  );

  const handleSelectedDayChange = (e: SelectChangeEvent, label: string) => {
    const newTabStates = JSON.parse(JSON.stringify(tabState));
    const selectedTabObject = newTabStates.find(
      (tab: TabStatesProps) => tab.label === label,
    )!;
    selectedTabObject.selectedFollowUPdays = e.target.value;
    dispatch(setTabStates({ tabState: [...newTabStates] }));
  };

  const handlePaste = (
    event: React.ClipboardEvent<HTMLDivElement>,
    isJd?: boolean,
  ) => {
    event.preventDefault();

    const clipboardData =
      event?.clipboardData || (window as any)?.clipboardData;
    const pastedHtml =
      clipboardData?.getData("text/html") ||
      clipboardData?.getData("text/plain");

    if (pastedHtml) {
      // let sanitizedHtml =   DOMPurify.sanitize(pastedHtml, {
      //   FORBID_ATTR: ['style', 'class', 'className'],
      //   FORBID_TAGS: ['input', 'iframe', 'button', 'a', 'img', 'svg']
      // });

      const sanitizedHtml = processHtmlForEmail(pastedHtml);

      // sanitizedHtml = sanitizedHtml.replace(/&nbsp;/g, ' ');
      console.log({ pastedHtml, sanitizedHtml });

      if (isJd) {
        if (jdRef?.current) {
          document.execCommand("insertHTML", false, sanitizedHtml);
        }
      } else {
        if (emailRef?.current) {
          document.execCommand("insertHTML", false, sanitizedHtml);
        }
      }

      setToastValue({
        isToastShown: true,
        toastMessage:
          "Formatted and rich content found in the email body has been removed.",
        toastKey: "pasted-text-info",
        toastSeverity: "info",
      });
    }
  };

  const onContentChange = (
    event: React.BaseSyntheticEvent,
    element: string,
  ) => {
    let targetedValue = event?.target?.value || "";
    targetedValue = targetedValue.replace(/&nbsp;/g, " ");

    if (element === "signature") {
      dispatch(setSignature({ signature: targetedValue }));
      return;
    }

    Object.entries(placeholderMapping).forEach(([text, htmlContent]) => {
      if (targetedValue.includes(text)) {
        targetedValue = targetedValue.replace(text, htmlContent);
      }
    });

    const newTabStates = handleContentEditHelper(
      tabState,
      jobDescription,
      targetedValue,
      selectedTab,
      element,
    );
    const selectedTabIndex = newTabStates.findIndex(
      (tab: TabStatesProps) => tab.label === selectedTab,
    );

    // if(targetedValue.includes('span')){
    //   targetedValue = replaceSpansWithPlaceholders(targetedValue)
    // }

    // if (targetedValue.includes('span')) {

    //   targetedValue = sanitizeHtml(targetedValue, profanityWordsInSelectedTab || []);
    // }

    if (element === "jd") {
      dispatch(setJobDescription({ jobDescription: targetedValue }));
    } else {
      if (element === "subject") {
        newTabStates[selectedTabIndex].subject = targetedValue;
      } else {
        newTabStates[selectedTabIndex].email = targetedValue;
      }
    }
    dispatch(setTabStates({ tabState: [...newTabStates] }));
    dispatch(
      setContentTypes({
        tabIndex: selectedTabIndex + 1,
        contentType: {
          contentType: "MANUAL",
          tabIndex: selectedTabIndex + 1,
        },
      }),
    );

    if (templateSelected) {
      dispatch(setTemplateSelected({ templateSelected: false }));
      dispatch(setCampaignTemplate({ campaignTemplate: "" }));
      dispatch(setTempalteForCampaign({ isTemplateForCampaignUsed: false }));
    }
  };

  const onDropDownClick = (name: string) => {
    setIsPlaceHolderAdded(true);
    if (activeElement === "email") {
      insertTextAtCursor(emailRef.current, name);
    } else if (activeElement === "subject") {
      insertTextAtCursor(subjectRef.current, name);
    } else if (activeElement === "signature") {
      insertTextAtCursor(signatureRef.current, name);
    } else {
      insertTextAtCursor(jdRef.current, name);
    }
    setTogglePlaceHolder(!togglePlaceHolder);
  };

  const handleFollowup = async ({
    index,
    tabStates,
    selectedTabLabel,
    isAutoGenerate,
  }: {
    index?: number;
    tabStates?: TabStatesProps[];
    selectedTabLabel?: string;
    isAutoGenerate?: boolean;
  }): Promise<TabStatesProps[]> => {
    const newTabStates = tabStates
      ? JSON.parse(JSON.stringify(tabStates))
      : JSON.parse(JSON.stringify(tabState));
    const selectedTabIndex = index
      ? index
      : newTabStates.findIndex(
          (tab: TabStatesProps) => tab.label === selectedTab,
        );

    setRevealingContent(true);

    try {
      const res = await postRephraseEmail({
        payload: {
          alreadyViewedKeys: [],
          campaignInitiationId,
          followUp: selectedTabIndex,
          type: "REPHRASE",
          isInitiationRequest: true,
        },
        domain,
      }).unwrap();
      // dispatch(setContentPersonalisationGenratedViaAI({ contentPersonalisationGenratedViaAI: 1 }));
      if (showGenerateByAiOnFollowUp) {
        const filteredGenerateByAiOnFollowUps =
          showGenerateByAiOnFollowUp?.filter((item) => item !== selectedTab) ||
          [];
        setShowGenerateByAiOnFollowUp(filteredGenerateByAiOnFollowUps);
      }
      if (res) {
        if (selectedTabLabel) {
          setRevealingContentTab(selectedTabLabel);
        } else {
          setRevealingContentTab(selectedTab);
        }

        const msg =
          res?.mailContent?.salutation +
          res?.mailContent?.body?.replaceAll("\\n", "<br>");
        const candidateFullName = res?.candidateFullName || "";
        newTabStates[selectedTabIndex].subject = res?.mailContent?.subject;
        newTabStates[selectedTabIndex].email = msg;
        newTabStates[selectedTabIndex].alreadyRephrased = true;
        newTabStates[selectedTabIndex].candidateFullName = candidateFullName;
        dispatch(setTabStates({ tabState: [...newTabStates] }));
        runContentReveal(
          msg
            ?.replaceAll("@fullName", candidateFullName)
            ?.replaceAll("@jobTitle", jobTitle)
            ?.replaceAll("@reqexpRange", `${minExperience}-${maxExperience}`) ||
            "",
          setRevealedEmail,
          setRevealingContent,
          () => {},
        );

        dispatch(
          setContentTypes({
            tabIndex: selectedTabIndex + 1,
            contentType: {
              contentType: "AI",
              tabIndex: selectedTabIndex + 1,
            },
          }),
        );

        return newTabStates; // Return the updated state
      }
    } catch (err) {
      console.log(err);
      setRevealingContent(false);
    }

    return newTabStates; // Return the updated state even if there's an error
  };

  const handleGenerateFollowup = () => {
    const newTabStates = JSON.parse(JSON.stringify(tabState));
    const selectedTabIndex = newTabStates.findIndex(
      (tab: TabStatesProps) => tab.label === selectedTab,
    );

    handleFollowup({
      index: selectedTabIndex,
      tabStates: newTabStates,
      selectedTabLabel: `Follow up-${selectedTabIndex}`,
      isAutoGenerate: true,
    });
  };

  const handleGenerate = (isContentChanged?: boolean) => {
    setRevealingContent(true);
    postAiEmail({
      campaignInitiationId,
      previewProfileNo: 1,
      isInitiationRequest: false,
    })
      .unwrap()
      .then((res) => {
        dispatch(
          setContentPersonalisationGenratedViaAI({
            contentPersonalisationGenratedViaAI: 1,
          }),
        );
        dispatch(setCampaignTemplate({ campaignTemplate: "" }));
        if (res) {
          setRevealingContentTab(selectedTab);

          const newTabStates = JSON.parse(JSON.stringify(tabState));
          // const selectedTabIndex = newTabStates.findIndex((tab: TabStatesProps) => tab.label === selectedTab)
          const msg =
            res?.mailContent?.salutation +
            res?.mailContent?.body?.replaceAll("\\n", "<br>");
          newTabStates[0].subject = res?.mailContent?.subject;
          newTabStates[0].email = msg;
          newTabStates[0].alreadyGenerated = true;
          if (newTabStates.length > 1) {
            const runFollowupsSequentially = async (
              tabStates: TabStatesProps[],
            ) => {
              let updatedTabStates = tabStates;
              for (let index = 1; index < tabStates.length; index++) {
                updatedTabStates = await handleFollowup({
                  index: index,
                  tabStates: updatedTabStates,
                  selectedTabLabel: `Follow up-${index}`,
                  isAutoGenerate: true,
                });
              }
            };

            runFollowupsSequentially(newTabStates);
          }
          runContentReveal(
            replaceDetails(msg, jobTitle, minExperience, maxExperience),
            setRevealedEmail,
            newTabStates?.length > 1 ? () => {} : setRevealingContent,
            () => {
              if (!isAiContentGenratedByPreValues?.current) {
                isAiContentGenratedByPreValues.current = true;
              }
            },
          );

          if (!isContentChanged || rephraseCount === 0) {
            dispatch(setRephraseCount({ rephraseCount: rephraseCount + 1 }));
            dispatch(setCurrentCount({ currentCount: rephraseCount + 1 }));
            dispatch(
              setSelectedContentPersonalisationKey({
                selectedContentPersonalisationKey: rephraseCount,
              }),
            );
            dispatch(
              setRephrasedEmailObj({
                rephrasedEmailObj: [
                  ...rephrasedEmailObj,
                  {
                    reprhaseIndex: rephraseCount + 1,
                    subject: res?.mailContent?.subject,
                    email: msg,
                  },
                ],
              }),
            );
          }

          dispatch(setTabStates({ tabState: [...newTabStates] }));
        }
      })
      .catch((err) => {
        console.log(err);
        setRevealingContent(false);
        setToastValue({
          isToastShown: true,
          toastMessage: err?.data?.message || "Error generating Email",
          toastKey: "ai-generate-error",
          toastSeverity: "error",
        });
      });

    setRevealingJD(true);
    aiJD({
      campaignId: initiationId,
      designation: jobTitle,
      locations: jobMode === "Remote" ? [] : jobLocation,
      maxExp: Number(maxExperience),
      minExp: Number(minExperience),
    })
      .unwrap()
      .then((res) => {
        dispatch(setJDGeneratedByAi({ JDGeneratedByAi: true }));
        if (res) {
          const msg = (
            <div>
              <header>Please find the job description for the role:</header>
              <br />
              <div>{res?.description || ""}</div>
              <br />
              <header>Responsibilities</header>
              <ul>
                {res?.responsibilities?.map((i: string) => (
                  <li>{i}</li>
                ))}
              </ul>
              <br />
              <header>Skills and Qualifications</header>
              <ul>
                {res?.skills_and_qualifications?.map((i: string) => (
                  <li>{i}</li>
                ))}
              </ul>
            </div>
          );
          runContentReveal(
            replaceDetails(
              renderToStaticMarkup(msg),
              jobTitle,
              minExperience,
              maxExperience,
            ) || "",
            setRevealedJD,
            setRevealingJD,
            () => {
              if (!isAiJDGeneratedByPreValues?.current) {
                isAiJDGeneratedByPreValues.current = true;
              }
            },
          );
          dispatch(
            setJobDescription({ jobDescription: renderToStaticMarkup(msg) }),
          );
        }
      })
      .catch((err) => {
        console.log(err);
        setRevealingJD(false);
        setToastValue({
          isToastShown: true,
          toastMessage:
            err?.data?.message || "Error generating job description",
          toastKey: "ai-jd-error",
          toastSeverity: "error",
        });
      });

    dispatch(
      setContentTypes({
        tabIndex: 1,
        contentType: {
          contentType: "AI",
          tabIndex: 1,
        },
      }),
    );
  };

  const handleRephrase = (currentTone: string) => {
    setRevealingContent(true);

    postRephraseEmail({
      payload: {
        alreadyViewedKeys: viewed,
        campaignInitiationId,
        followUp: 0,
        type: currentTone,
        isInitiationRequest: false,
      },
      domain,
    })
      .unwrap()
      .then((res) => {
        dispatch(
          setContentPersonalisationType({
            contentPersonalisationType: currentTone,
          }),
        );
        dispatch(
          setContentPersonalisationGenratedViaAI({
            contentPersonalisationGenratedViaAI: 1,
          }),
        );
        if (res) {
          dispatch(setPreviewNo(1));
          dispatch(
            setContentPersonalisationType({
              contentPersonalisationType: currentTone,
            }),
          );
          setRevealingContentTab(selectedTab);

          const newTabStates = JSON.parse(JSON.stringify(tabState));
          const selectedTabIndex = newTabStates.findIndex(
            (tab: TabStatesProps) => tab.label === selectedTab,
          );
          const msg =
            res?.mailContent?.salutation +
            res?.mailContent?.body?.replaceAll("\\n", "<br>");
          newTabStates[selectedTabIndex].subject = res?.mailContent?.subject;
          newTabStates[selectedTabIndex].email = msg;
          runContentReveal(
            replaceDetails(msg, jobTitle, minExperience, maxExperience) || "",
            setRevealedEmail,
            setRevealingContent,
            () => {},
          );

          dispatch(setViewed({ viewed: [...viewed, res.key] }));
          if (currentTone == "REPHRASE") {
            dispatch(setRephraseCount({ rephraseCount: rephraseCount + 1 }));
            dispatch(setCurrentCount({ currentCount: rephraseCount + 1 }));
            dispatch(
              setSelectedContentPersonalisationKey({
                selectedContentPersonalisationKey: rephraseCount,
              }),
            );
            dispatch(
              setRephrasedEmailObj({
                rephrasedEmailObj: [
                  ...rephrasedEmailObj,
                  {
                    reprhaseIndex: rephraseCount + 1,
                    subject: res?.mailContent?.subject,
                    email: msg,
                  },
                ],
              }),
            );
          } else {
            const tempEmailObj = rephrasedEmailObj.filter(
              (i: any) => i.reprhaseIndex !== currentCount,
            );
            dispatch(
              setRephrasedEmailObj({
                rephrasedEmailObj: [
                  ...tempEmailObj,
                  {
                    reprhaseIndex: currentCount,
                    subject: res?.mailContent?.subject,
                    email: msg,
                  },
                ],
              }),
            );
          }
          dispatch(setTabStates({ tabState: [...newTabStates] }));
        }
      })
      .catch((err) => {
        console.log(err);
        setRevealingContent(false);
        setToastValue({
          isToastShown: true,
          toastMessage: err?.data?.message || "Error rephrasing Email",
          toastKey: "ai-rephrase-error",
          toastSeverity: "error",
        });
      });

    setRevealingJD(true);

    aiJD({
      campaignId: initiationId,
      designation: jobTitle,
      locations: jobMode === "Remote" ? [] : jobLocation,
      maxExp: Number(maxExperience),
      minExp: Number(minExperience),
    })
      .unwrap()
      .then((res) => {
        dispatch(setJDGeneratedByAi({ JDGeneratedByAi: true }));
        if (res) {
          const msg = (
            <div>
              <header>Please find the job description for the role:</header>
              <br />
              <div>{res?.description || ""}</div>
              <br />
              <header>Responsibilities</header>
              <ul>
                {res?.responsibilities?.map((i: string) => (
                  <li>{i}</li>
                ))}
              </ul>
              <br />
              <header>Skills and Qualifications</header>
              <ul>
                {res?.skills_and_qualifications?.map((i: string) => (
                  <li>{i}</li>
                ))}
              </ul>
            </div>
          );
          runContentReveal(
            replaceDetails(
              renderToStaticMarkup(msg),
              jobTitle,
              minExperience,
              maxExperience,
            ),
            setRevealedJD,
            setRevealingJD,
            () => {},
          );
          dispatch(
            setJobDescription({ jobDescription: renderToStaticMarkup(msg) }),
          );
        }
      })
      .catch((err) => {
        console.log(err);
        setRevealingJD(false);
        setToastValue({
          isToastShown: true,
          toastMessage:
            err?.data?.message || "Error generating job description",
          toastKey: "ai-jd-error",
          toastSeverity: "error",
        });
      });
  };

  const navigateRephrase = (curr: number) => {
    dispatch(setCurrentCount({ currentCount: curr }));
    dispatch(
      setSelectedContentPersonalisationKey({
        selectedContentPersonalisationKey: curr - 1,
      }),
    );
    const emailObj = rephrasedEmailObj.filter(
      (idx: any) => idx.reprhaseIndex == curr,
    );
    const newTabStates = JSON.parse(JSON.stringify(tabState));
    const selectedTabIndex = newTabStates.findIndex(
      (tab: TabStatesProps) => tab.label === selectedTab,
    );
    newTabStates[selectedTabIndex].subject = emailObj[0]?.subject;
    newTabStates[selectedTabIndex].email = emailObj[0]?.email;
    dispatch(setTabStates({ tabState: [...newTabStates] }));
  };

  const handleContentClick = (e: any, type: string) => {
    setActiveElement(type);
    const element = e.target;

    if (
      element.tagName.toLowerCase() === "svg" ||
      element.tagName.toLowerCase() === "path"
    ) {
      const campaignPlaceholderText =
        element.tagName.toLowerCase() === "svg"
          ? element.parentElement?.parentElement?.textContent
              ?.trim()
              .split(":")[0]
          : element.parentElement?.parentElement?.parentElement?.textContent
              ?.trim()
              .split(":")[0];
      const placeholderData = {
        top: e.pageY - 50,
        left: e.pageX + 20,
        content: campaignPlaceholderText,
        value: campiagnPlaceholderValues[campaignPlaceholderText],
      };
      setPlaceHolderDetails(placeholderData);
      setShowCampaignPlaceholder(true);
    }
  };

  const handleCampiagnInputDetailsUpdate = (
    jobTitle: string,
    value: string,
  ) => {
    const updatedInputDetails = { ...campiagnPlaceholderValues };
    const prevValue =
      updatedInputDetails[jobTitle as keyof CampaignPlaceHolderDetails];
    updatedInputDetails[jobTitle as keyof CampaignPlaceHolderDetails] = value;
    setCampaignPlaceholderValues(updatedInputDetails);
    const newTabStates: TabStatesProps[] = updatedTabStateAfterPlaceholderInput(
      tabState,
      jobTitle,
      prevValue,
      value,
    );
    dispatch(setTabStates({ tabState: [...newTabStates] }));
    setShowCampaignPlaceholder(false);
  };

  const showGenerateViaButton =
    !isCampaignTemplateClicked &&
    showGenerateByAiOnFollowUp.includes(selectedTab) &&
    selectedTab !== "Email";

  const runContentRevealForTemplates = (msg: string) => {
    if (!isAiContentGenratedByPreValues?.current) {
      isAiContentGenratedByPreValues.current = true;
    }
    const revealContent =
      replaceDetails(
        msg,
        jobTitle,
        minExperience,
        maxExperience,
        selectedTabData?.candidateFullName || "",
      ) || "";

    setRevealingContent(false);
    setRevealedEmail(revealContent);
  };

  const handleReset = () => {
    if (selectedTab === "Email") {
      if (resetDetails?.resetCount === 0) {
        if (contentPersonalisationGenratedViaAI) {
          // condition to check if content type is ai
          dispatch(
            setResetDetails({
              isResetClicked: true,
              originalContent: "AI",
            }),
          );
        } else if (templateSelected) {
          // condition to check if content type is template
          dispatch(
            setResetDetails({
              isResetClicked: true,
              originalContent: "TEMPLATE",
            }),
          );
        } else if (selectedTabData?.email) {
          // condition to check if content type is manual
          dispatch(
            setResetDetails({
              isResetClicked: true,
              originalContent: "MANUAL",
            }),
          );
        }
      } else {
        dispatch(
          setResetDetails({
            isResetClicked: true,
          }),
        );
      }

      dispatch(setJDGeneratedByAi({ JDGeneratedByAi: false }));
      dispatch(setContentPersonalisationGenratedViaAI(0));
    }
    if (selectedTab !== "Email") {
      setShowGenerateByAiOnFollowUp((prev) => [...prev, selectedTab]);
    }
    dispatch(resetEmailData());

    setOpenResetModal(false);

    let emailStage = "Primary";

    if (selectedTab === "Email") {
      emailStage = "Primary";
    } else {
      emailStage = `F${selectedTabData?.stepNumber ? selectedTabData.stepNumber - 1 : ""}`;
    }

    // clevertap event on confirm reset
    eventPushFunction({
      eventName: "Reset_Confirmed",
      data: {
        original_email_type:
          contentTypes[selectedTabData?.stepNumber || 0]?.contentType ||
          "MANUAL",
        email_stage: emailStage,
        xcode: userDetails?.x_code || "",
        subuid: userDetails?.recruiter_id || "",
      },
    });

    if (selectedTabData?.stepNumber) {
      dispatch(
        setContentTypes({
          tabIndex: selectedTabData?.stepNumber,
          contentType: {
            contentType: "MANUAL",
            tabIndex: selectedTabData?.stepNumber,
          },
        }),
      );
    }
  };

  const handleResetModalOpen = () => {
    if (
      selectedTabData?.alreadyGenerated ||
      selectedTabData?.alreadyRephrased
    ) {
      setOpenResetModal(true);
    } else {
      handleReset();
    }

    // clevertap event on reset button click
    eventPushFunction({
      eventName: "Reset_Clicked",
      data: {
        page_name: "Email campaign creation page",
      },
    });
  };

  const isAiUsed =
    selectedTabData?.alreadyRephrased! ||
    selectedTabData?.alreadyGenerated! ||
    (selectedTab === "Email" && JDGeneratedByAi);

  const handlePassiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPassiveJobs(!passiveJobsEnabled));
  };

  const handlePreviewClick = () => {
    dispatch(setJobPreview(true));
  };
  const getTitle = () => {
    if (disablePassiveJobs) {
      return (
        <CustomList>
          <CustomText>
            Campaigns will be posted as a job only when the campaign is
            generated by AI
          </CustomText>
        </CustomList>
      );
    }

    if (!passiveJobsEnabled) {
      return (
        <CustomList>
          <CustomText>
            You may miss out on unlocking access to a broader, active talent
            pool beyond your current campaign
          </CustomText>
        </CustomList>
      );
    }
    return "";
  };

  useEffect(() => {
    if (disablePassiveJobs) {
      dispatch(setPassiveJobs(false));
    }
  }, [disablePassiveJobs, dispatch]);

  return (
    <Container>
      <Tabs
        selectedTab={selectedTab}
        handleDeleteFollowUp={handleOpenModal}
        handleAddFollowUp={handleAddFollowUp}
        handleTabClick={handleTabClick}
        tabState={tabState}
        handleSelectedDayChange={handleSelectedDayChange}
        disabled={revealingContent || revealingJD}
      />
      {showFollowUpBanner && (
        <FollowUpBanner
          onClose={() => setIsBannerClose(true)}
          bannerText={`Follow-up is scheduled automatically in case of no candidate response. ${isBridgeCustomer ? "[This will consume additional inventory]" : ""}`}
        />
      )}

      <Subject
        subject={selectedTabData?.subject || ""}
        handleSubChange={onContentChange}
        onDropDownClick={onDropDownClick}
        setActiveElement={handleContentClick}
        // setElement={() => setActiveElement('subject')}
        ref={subjectRef}
        alreadyGenerated={selectedTabData?.alreadyGenerated!}
        alreadyRephrased={selectedTabData?.alreadyRephrased!}
        candidateFullName={selectedTabData?.candidateFullName || ""}
      />
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          flexGrow: 1,
        }}
      >
        <EmailContent
          email={email}
          handleContentChange={onContentChange}
          setActiveElement={handleContentClick}
          ref={emailRef}
          alreadyGenerated={selectedTabData?.alreadyGenerated!}
          alreadyRephrased={selectedTabData?.alreadyRephrased!}
          revealingContent={revealingContent}
          revealingContentTab={revealingContentTab}
          revealedEmail={!revealingContent ? revealedEmail : ""}
          candidateFullName={selectedTabData?.candidateFullName || ""}
          handlePaste={handlePaste}
        />
        {selectedTab === "Email" && isAiUsed && (
          <ContentEditable
            className="job_description_box"
            style={{
              width: "100%",
              outline: "none",
              height: "100%",
              fontSize: "1.4rem",
              lineHeight: "2.2rem",
              padding: "12px 16px",
              boxSizing: "border-box",
              minHeight: revealingJD ? "63rem" : "0",
            }}
            innerRef={jdRef!}
            onChange={(e) => onContentChange(e, "jd")}
            html={
              revealingJD
                ? revealedJD
                : replaceDetails(
                    jobDescription,
                    jobTitle,
                    minExperience,
                    maxExperience,
                  )
            }
            onClick={(e) => handleContentClick(e, "jd")}
            onFocus={(e) => handleContentClick(e, "jd")}
            onBlur={(e) => null}
            onPaste={(e) => handlePaste(e, true)}
          />
        )}
        {/* <Box sx={{  display: 'flex', padding: '24px', justifyContent: 'flex-end', position: 'relative', top: '10px', zIndex: '1'}} > */}
        <Box
          sx={{
            display: "flex",
            padding: "24px",
            justifyContent: "flex-end",
            position: isAiUsed ? "sticky" : "relative",
            bottom: isAiUsed ? "24px" : "inherit",
            top: isAiUsed ? "inherit" : "24px",
            zIndex: "1",
          }}
        >
          {(showGenerateViaButton ||
            (selectedTab === "Email" && rePhraseFeature)) && (
            <GenerateByAiButton
              handleGenerate={
                selectedTab === "Email"
                  ? handleGenerate
                  : handleGenerateFollowup
              }
              handleRephrase={handleRephrase}
              disableGenerateContent={disableGenerateContent}
              handleFollowup={handleFollowup}
              navigateRephrase={navigateRephrase}
              currentTab={selectedTab}
              generateOrRephrase={
                rephraseCount == 0 || selectedTab != "Email"
                  ? "generate"
                  : "rephrase"
              }
              rephraseCount={rephraseCount}
              buttonWidth={rephraseCount > 1 ? "300px" : "200px"}
              currentCount={currentCount}
              // revealingContent={revealingContent}
              revealingContent={revealingContent || revealingJD}
              selectedTones={selectedTones}
              setSelectedTones={setSelectedTones}
              selectedLengths={selectedLengths}
              setSelectedLengths={setSelectedLengths}
              JDGeneratedByAi={JDGeneratedByAi}
              rePhraseFeature={rePhraseFeature}
            />
          )}

          <ResetContainer
            onClick={
              revealingContent || revealingJD ? undefined : handleResetModalOpen
            }
          >
            <Box>
              <EraseIcon
                stroke={
                  revealingContent || revealingJD
                    ? COLOR_MAP.BORDER_GREY
                    : "black"
                }
              />
            </Box>
            <Box
              sx={{
                lineHeight: "20px",
                fontSize: "14px",
                color:
                  revealingContent || revealingJD
                    ? COLOR_MAP.BORDER_GREY
                    : "inherit",
              }}
            >
              Reset
            </Box>
          </ResetContainer>
        </Box>
        {(revealingJD || revealingContent) && !isContentRevealedByTemplates ? (
          <GeneratedByAiStrip />
        ) : null}
      </div>
      <EmailFooter
        signatureRef={signatureRef}
        // jdRef={jdRef}
        handleContentChange={onContentChange}
        signatureContent={signature || ""}
        jdContent={jobDescription || ""}
        setElement={(element: string) => setActiveElement(element)}
      />
      {showForbiddenStrip ? <ForbiddenWordsStrip /> : null}

      {/* <ProhibitedContent
      value={activeActionButton}
      handleActionsButtonsChange={handleActionsButtonsChange}
    /> */}
      {showPassiveJobs ? (
        <PassiveJobsContainer>
          {/* {PASSIVE_JOBS_ICON} */}
          <div
            style={{
              ...(disablePassiveJobs && {
                color: COLOR_MAP.GREY_1,
              }),
            }}
          >
            Enable inbound applications by automatically posting this campaign
            as a job
          </div>

          <BootstrapTooltip placement="top-end" title={getTitle()}>
            <Box>
              <PassiveSwitch
                checked={passiveJobsEnabled}
                onChange={handlePassiveChange}
                disabled={disablePassiveJobs}
              />
            </Box>
          </BootstrapTooltip>

          {passiveJobsEnabled ? (
            <Button
              variant="text"
              onClick={() => {
                if (jobTitle === "") {
                  setToastValue({
                    isToastShown: true,
                    toastMessage: "Add Designation to preview Job Posting",
                    toastKey: "campaign-title-error",
                    toastSeverity: "error",
                  });
                  return;
                } else {
                  handlePreviewClick();
                }
              }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "12px 16px",
                fontSize: "12px",
                fontWeight: 400,
                backgroundColor: "#FAFBFC",
                color: "#6E00BE",

                ":hover": {
                  backgroundColor: "#FAFBFC",
                  color: "#6E00BE",
                },
              }}
              id="previewJobPost"
              aria-label="Preview job posts"
            >
              Preview job post
            </Button>
          ) : null}
        </PassiveJobsContainer>
      ) : null}
      <BasicModal
        handleOpen={handleOpenModal}
        handleClose={() => setOpenModal(false)}
        // action='Delete'
        action="Remove"
        flipCTA={true}
        flipActions={true}
        handleAction={handleDeleteModalButton}
        open={openModal}
        heading="Are you sure?"
        content={
          <div style={{ paddingTop: "1.6rem" }}>
            Removing follow-ups may cut response rates by{" "}
            <span style={{ color: "red" }}>
              <strong>2x</strong>
            </span>
          </div>
        }
      />
      {/* <BasicModal
      handleClose={() => setOpenResetModal(false)}
      action='Yes, reset'
      cancelAction={`No, don't reset`}
      handleAction={handleReset}
      open={openResetModal}
      heading='Are you Sure ?'
      content={<span>Ai content gives you <span style={{color: 'green'}}><strong>50%</strong></span> better Response Rates <br />We suggest you to stick with it.</span>}
    /> */}
      <BasicModal
        handleClose={() => setOpenResetModal(false)}
        action="Yes, reset"
        cancelAction={`No, don't reset`}
        // flipCTA={true}
        // flipActions={true}
        handleAction={handleReset}
        open={openResetModal}
        heading={
          selectedTab === "Email"
            ? "Do you want to reset your email content?"
            : "Do you want to reset your follow-up content?"
        }
        dimensions={{ height: 220, width: 360 }}
        content={
          <div>
            {selectedTab === "Email"
              ? "Resetting removes your email content. You can pick a template, create your own, or generate with AI again."
              : "Current follow-up content will be removed. You can create your own or use AI to generate new content."}
          </div>
        }
      />
      {showCampaignPlaceholder ? (
        <CampaignPlaceholderInput
          handleModalClose={() => setShowCampaignPlaceholder(false)}
          handleCampiagnInputDetailsUpdate={handleCampiagnInputDetailsUpdate}
          placeHolderDetails={placeHolderDetails}
        />
      ) : null}
    </Container>
  );
};

export default Email;
